
import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface ActionButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  label: string;
  onClick?: (e: React.MouseEvent) => void;
  to?: string;
  target?: string;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  className?: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({ 
  icon, 
  label, 
  onClick, 
  to, 
  target = "_blank",
  variant = "ghost",
  className = "",
  ...props
}) => {
  const buttonContent = (
    <Button 
      variant={variant} 
      size="sm" 
      onClick={onClick} 
      className={cn(
        "h-8 w-8 p-0 rounded-full",
        variant === "ghost" ? "text-muted-foreground hover:text-foreground hover:bg-muted" : "",
        variant === "destructive" ? "hover:bg-destructive/10 hover:text-destructive" : "",
        className
      )}
      {...props}
    >
      <span className="sr-only">{label}</span>
      {icon}
    </Button>
  );

  const wrappedButton = (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {to ? (
            <Link to={to} target={target}>
              {buttonContent}
            </Link>
          ) : (
            buttonContent
          )}
        </TooltipTrigger>
        <TooltipContent className="bg-background/95 backdrop-blur-md border border-border/40 shadow-apple-md">
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return wrappedButton;
};

export default ActionButton;
